import React, { ReactElement } from 'react';
import {
  Box,
  Grid,
  Stack,
  SvgIcon,
  SxProps,
  Tab,
  Typography,
} from '@mui/material';
import { TabContext, TabPanel, TabList } from '@mui/lab';
import { withTranslation, WithTranslation } from 'react-i18next';
import { colors } from 'styles/colors';
import { KFTooltip } from '@kf/ic2fx';
import { ReactComponent as infoIcon } from 'assets/InfoIcon.svg';

export type TabItem = {
  count?: number;
  i18nKey: string;
  component?: React.ReactElement;
};

interface ITabsProps {
  currentTab: string;
  setCurrentTab: (_: any, newValue: string) => void;
  tabs: TabItem[];
  gridSx?: any;
  tabPanelSx?: any;
  isViewAllEnable?: boolean;
  onViewAllClick?: () => void;
  isNotTab?: boolean;
  coursesCount?: number;
  sectionName?: string;
  tabListItemSx?: SxProps;
  tabListItemTextSx?: SxProps;
  activeTabListItemTextSx?: any;
  tabsMultiFilter?: any;
  isTooltip?: boolean;
  showTabTopBorder?: boolean;
  tabPanelWidth?: React.CSSProperties;
  scrollBtnProps?: React.CSSProperties;
  tabWidth?: string;
  muiTabIndicator?: React.CSSProperties;
  disableTabs?: boolean;
  tabIndicatorStyleProps?: React.CSSProperties;
}

export type TabsProps = ITabsProps & WithTranslation;

export const Tabs = ({
  currentTab,
  setCurrentTab,
  tabs,
  gridSx,
  tabPanelSx,
  t: translate,
  isViewAllEnable,
  onViewAllClick,
  isNotTab,
  coursesCount,
  sectionName,
  tabListItemSx,
  tabsMultiFilter,
  isTooltip,
  showTabTopBorder,
  tabListItemTextSx,
  activeTabListItemTextSx,
  tabPanelWidth,
  scrollBtnProps,
  tabWidth,
  muiTabIndicator,
  disableTabs,
  tabIndicatorStyleProps,
}: TabsProps) => {
  const chkTabsEnabled = !disableTabs || tabs?.some((e) => e?.count);
  const renderLabel = (label: string, index: string, count?: number) => (
    <Typography
      fontWeight={chkTabsEnabled && currentTab === index ? 'bold' : 'regular'}
      align='left'
      id={`${sectionName}-tab-${index}`}
      data-testid={`${sectionName}-tab-${index}`}
      data-activated={currentTab === index}
      tabIndex={isNotTab ? 0 : -1}
      aria-label={`${translate(label)}-tab`}
      sx={{
        pt: 1,
        ':focus': {
          width: 'fit-content',
        },
        color: chkTabsEnabled && currentTab === index ? colors.primaryDark : '',
        ...tabListItemTextSx,
        ...(currentTab === index && activeTabListItemTextSx),
      }}
    >
      {translate(label)} {count !== undefined && count >= 0 && `(${count})`}
    </Typography>
  );

  return (
    <Grid sx={gridSx}>
      <TabContext value={currentTab}>
        <Grid container justifyContent={isTooltip ? 'space-between' : ''}>
          <Grid
            item
            sx={{
              width: tabWidth || '490px',
              display: tabsMultiFilter && 'flex',
              gap: tabsMultiFilter && '8px',
              ':focus': { width: 'fit-content' },
              ...tabPanelWidth,
            }}
          >
            {!isNotTab ? (
              <Box display='flex' justifyContent='start' width='100%'>
                <TabList
                  data-testid={`${sectionName}-tabList`}
                  id={`${sectionName}-tabList`}
                  onChange={setCurrentTab}
                  textColor='inherit'
                  TabIndicatorProps={{
                    children: chkTabsEnabled ? (
                      <span style={{ ...tabIndicatorStyleProps }} />
                    ) : (
                      <></>
                    ),
                  }}
                  sx={{
                    '& .MuiTabs-indicator': {
                      display: 'flex',
                      justifyContent: 'center',
                      backgroundColor: 'transparent',
                      top: 0,
                      '& > span': {
                        maxWidth: 30,
                        width: '100%',
                        backgroundColor: !showTabTopBorder
                          ? colors.primaryDark
                          : '',
                        ...muiTabIndicator,
                      },
                    },
                    minHeight: 0,
                    width: 'fit-content',
                    maxWidth: { xs: '95%', sm: '100%', md: '100%', lg: '100%' },
                  }}
                  variant='scrollable'
                  allowScrollButtonsMobile
                  TabScrollButtonProps={{
                    style: {
                      width: 'auto',
                      maxWidth: '20px',
                      display: 'flex',
                      ...scrollBtnProps,
                    },
                  }}
                >
                  {tabs.map((tab, index) => {
                    const tabIndex = index.toString();

                    return (
                      <Tab
                        key={`shared-tab-${tabIndex}`}
                        value={tabIndex}
                        label={renderLabel(tab.i18nKey, tabIndex, tab.count)}
                        role='presentation'
                        aria-label={`${translate(tab.i18nKey)}`}
                        id={`tab-${tabIndex}`}
                        data-testid={`tab-${tabIndex}`}
                        tabIndex={0}
                        disabled={disableTabs ? tab?.count === 0 : false}
                        sx={{
                          padding: 0,
                          textTransform: 'capitalize',
                          alignItems: 'baseline',
                          minWidth: 0,
                          minHeight: 0,
                          mr: 2,
                          '&:focus': {
                            border: `2px solid ${colors?.accessibility?.focus}`,
                            maxHeight: 28,
                          },
                          ...tabListItemSx,
                        }}
                      />
                    );
                  })}
                </TabList>
              </Box>
            ) : (
              <Stack
                id={`${sectionName}-wrapper-tabList`}
                data-testid={`${sectionName}-wrapper-tabList`}
              >
                {tabs.map((tab, index) => {
                  const tabIndex = index.toString();
                  return (
                    <Stack key={`stack-label-${index}`}>
                      {renderLabel(tab.i18nKey, tabIndex, tab.count)}
                    </Stack>
                  );
                })}
              </Stack>
            )}
            {tabsMultiFilter || ''}
          </Grid>
          {isTooltip && (
            <Grid item display={'flex'} alignItems={'center'}>
              <KFTooltip
                body={translate('realTimeProgress')}
                color='light'
                placement='left'
                title=''
              >
                <SvgIcon
                  aria-labelledby={'info-icon'}
                  viewBox='0 0 16 16'
                  tabIndex={0}
                  sx={{
                    width: 16,
                    height: 16,
                    alignSelf: 'center',
                    cursor: 'pointer',
                    fill: 'none',
                    '&:focus': { width: 'fit-content' },
                  }}
                  component={infoIcon}
                />
              </KFTooltip>
            </Grid>
          )}

          {isViewAllEnable && (
            <Grid
              item
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: { xs: 2, sm: 0, md: 0, lg: 0 },
              }}
            >
              <Typography
                id='training-tabs-view-all'
                data-testid='training-tabs-view-all'
                className='kfic2-bodyTextLink'
                aria-label={'training-tabs-view-all'}
                onClick={onViewAllClick}
                onKeyPress={(event: any) => {
                  /* istanbul ignore else */
                  if (event.key === 'Enter' && onViewAllClick) onViewAllClick();
                }}
                tabIndex={0}
                role='navigation'
                sx={{
                  ':focus': {
                    width: 'fit-content',
                  },
                }}
              >
                {`${translate('viewAll')} (${coursesCount})`}
              </Typography>
            </Grid>
          )}
        </Grid>
        {tabs.map((tab, index) => {
          const tabIndex = index.toString();

          return (
            <TabPanel
              value={tabIndex}
              key={`shared-tab-component-${tabIndex}`}
              sx={{
                pl: 0,
                ...tabPanelSx,
                display: tab?.component ? undefined : 'none',
                pr: 0,
              }}
            >
              {tab?.component || ''}
            </TabPanel>
          );
        })}
      </TabContext>
    </Grid>
  );
};

export default withTranslation()(Tabs);
